import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PseudoProject } from '../../types/todo.js';
import * as pseudoProjectApi from '../../services/pseudo-project-api.js';

@customElement('cp-pseudo-project-selector')
export class CpPseudoProjectSelector extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) selectedId = 0;

  @state() private _projects: PseudoProject[] = [];
  @state() private _loading = true;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    void this.loadProjects();
    document.addEventListener('pseudo-project-created', this._onProjectCreated);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('pseudo-project-created', this._onProjectCreated);
  }

  async loadProjects(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      this._projects = await pseudoProjectApi.getPseudoProjects();
      // Auto-select first project if none selected
      if (!this.selectedId && this._projects.length > 0) {
        this._selectProject(this._projects[0]);
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Laden fehlgeschlagen';
    } finally {
      this._loading = false;
    }
  }

  private _onProjectCreated = (e: Event): void => {
    const detail = (e as CustomEvent).detail;
    if (detail?.project) {
      this._projects = [...this._projects, detail.project as PseudoProject];
      this._selectProject(detail.project as PseudoProject);
    }
  };

  private _selectProject(project: PseudoProject): void {
    this.selectedId = project.id;
    this.dispatchEvent(new CustomEvent('pseudo-project-changed', {
      bubbles: true,
      composed: true,
      detail: { project },
    }));
  }

  private _onTabClick(project: PseudoProject): void {
    if (project.id === this.selectedId) return;
    this._selectProject(project);
  }

  private _onAddClick(): void {
    document.dispatchEvent(new CustomEvent('pseudo-project-add-request'));
  }

  private _renderEmpty() {
    return html`
      <div class="cp-pseudo-project-selector__empty">
        <p class="cp-pseudo-project-selector__empty-text">Erstelle dein erstes Pseudo-Projekt, um To-Dos zu verwalten</p>
        <button class="cp-pseudo-project-selector__empty-btn" @click=${this._onAddClick}>
          Pseudo-Projekt erstellen
        </button>
      </div>
    `;
  }

  private _renderTabs() {
    return html`
      <div class="cp-pseudo-project-selector__tabs">
        ${this._projects.map(p => html`
          <button
            class="cp-pseudo-project-selector__tab ${p.id === this.selectedId ? 'cp-pseudo-project-selector__tab--active' : ''}"
            @click=${() => this._onTabClick(p)}
          >${p.name}</button>
        `)}
        <button class="cp-pseudo-project-selector__tab cp-pseudo-project-selector__tab--add" @click=${this._onAddClick}>
          + Neu
        </button>
      </div>
    `;
  }

  render() {
    if (this._loading) {
      return html`<div class="cp-pseudo-project-selector__loading">Lade Projekte...</div>`;
    }

    if (this._error) {
      return html`<div class="cp-pseudo-project-selector__error">${this._error}</div>`;
    }

    if (this._projects.length === 0) {
      return this._renderEmpty();
    }

    return this._renderTabs();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-pseudo-project-selector': CpPseudoProjectSelector;
  }
}
